export type LockedBy = 'merchant' | 'sync'

/** Partial option as stored/returned by APIs before normalization. */
export type CurrencyOptionInput = {
  rate?: number
  amount: number
  lock?: boolean
  lockedBy?: LockedBy
  date?: string
}

/** Normalized option — always has rate/lock/date filled. */
export type CurrencyOption = {
  rate: number
  amount: number
  lock: boolean
  lockedBy?: LockedBy
  date: string
}

export type CurrencyOptionsInput = Record<string, CurrencyOptionInput>
export type CurrencyOptions = Record<string, CurrencyOption>

/** Uppercase + case-insensitive dedupe. */
export const normalizeCurrencyCodes = (currencies: string[] | null | undefined): string[] => {
  if (!currencies?.length) return []
  const seen = new Set<string>()
  return currencies.reduce<string[]>((acc, code) => {
    const upper = code.toUpperCase()
    if (seen.has(upper)) return acc
    seen.add(upper)
    return [...acc, upper]
  }, [])
}

const pickPreferredOption = (current: CurrencyOption, candidate: CurrencyOption): CurrencyOption => {
  if (candidate.amount > current.amount) return candidate
  if (candidate.amount < current.amount) return current
  if (candidate.lock && !current.lock) return candidate
  return current
}

/** Merge dual-case keys (`usd` + `USD`) into a single UPPERCASE entry. */
export const normalizeCurrencyOptions = (
  options: CurrencyOptionsInput | null | undefined,
): CurrencyOptions => {
  const now = new Date().toISOString()
  return Object.entries(options ?? {}).reduce<CurrencyOptions>((acc, [key, option]) => {
    const upper = key.toUpperCase()
    const next: CurrencyOption = {
      rate: option.rate ?? 1,
      amount: option.amount,
      lock: option.lock ?? false,
      date: option.date ?? now,
      ...(option.lockedBy && { lockedBy: option.lockedBy }),
    }
    const existing = acc[upper]
    return {
      ...acc,
      [upper]: existing ? pickPreferredOption(existing, next) : next,
    }
  }, {})
}

export const getCurrencyOption = <T extends { amount: number }>(
  options: Record<string, T> | null | undefined,
  code?: string | null,
): { currency: string; option: T } | null => {
  if (!options) return null
  const entries = Object.entries(options)
  if (entries.length === 0) return null

  const pickBest = (matches: Array<[string, T]>) => {
    const best = matches.reduce((winner, candidate) =>
      candidate[1].amount > winner[1].amount ? candidate : winner,
    )
    return { currency: best[0].toUpperCase(), option: best[1] }
  }

  if (code) {
    const upper = code.toUpperCase()
    const matches = entries.filter(([key]) => key.toUpperCase() === upper)
    if (matches.length > 0) return pickBest(matches)
  }

  const nonZero = entries.filter(([, option]) => option.amount > 0)
  return pickBest(nonZero.length > 0 ? nonZero : entries)
}

export const getCurrencyAmount = (
  options: Record<string, { amount: number }> | null | undefined,
  code?: string | null,
): number | null => getCurrencyOption(options, code)?.option.amount ?? null
